import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { GitBranch, Save, Loader2, Trash2, CreditCard, ExternalLink, CalendarClock, Pencil, Flag, FileDown, QrCode } from "lucide-react";
import { API } from "@/lib/api";
import api, { mediaUrl } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { useAuth } from "@/context/AuthContext";
import { StepDetails, StepRegistration, StepSettings } from "./FormSteps";
import { EMPTY_TOURNAMENT, T_STATUSES, FORMAT_LABEL, fmtDateTime, inputCls } from "./constants";
import BracketView from "./BracketView";
import MatchEditor from "./MatchEditor";
import TeamsList from "./TeamsList";
import SharePanel from "./SharePanel";
import SlotBoard from "./SlotBoard";
import ReportsPanel, { ReportBadge } from "./ReportsPanel";
import TeamEditor from "./TeamEditor";
import ChampionBanner from "./ChampionBanner";
import CheckoutModal from "./CheckoutModal";

const TABS = ["Details", "Teams", "Bracket & Scores", "Schedule", "Reports", "Share"];
const pickForm = (t) => Object.fromEntries(Object.keys(EMPTY_TOURNAMENT).map((k) => [k, t[k] ?? EMPTY_TOURNAMENT[k]]));

function DetailsTab({ t, onSaved }) {
  const [f, setF] = useState(pickForm(t));
  const [busy, setBusy] = useState(false);
  const upd = (p) => setF((c) => ({ ...c, ...p }));
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/manage/tournaments/${t.id}`, f); onSaved(r.data); toast.success("Tournament saved"); }
    catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Please check the form"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-8">
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4"><h3 className="font-bold text-white">Overview & Schedule</h3><StepDetails f={f} upd={upd} />
          <div><label className="block text-xs font-semibold text-white/50 mb-1.5">Game</label><input value={f.game} onChange={(e) => upd({ game: e.target.value })} className={inputCls} data-testid="mgr-game" /></div>
        </div>
        <div className="space-y-8"><div><h3 className="font-bold text-white mb-4">Registration & Rules</h3><StepRegistration f={f} upd={upd} /></div>
          <div><h3 className="font-bold text-white mb-4">Format & Status</h3><StepSettings f={f} upd={upd} /></div></div>
      </div>
      <button onClick={save} disabled={busy} data-testid="mgr-save" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-60 hover:brightness-110">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes
      </button>
    </div>
  );
}

function BracketTab({ t, setT, onEdit }) {
  const [busy, setBusy] = useState(false);
  const has = (t.bracket?.matches || []).length > 0;
  const hasPlayoffs = (t.bracket?.matches || []).some((m) => m.section === "playoffs");
  const run = async (path, confirmMsg) => {
    if (confirmMsg && !window.confirm(confirmMsg)) return;
    setBusy(true);
    try { const r = await api.post(`/manage/tournaments/${t.id}/${path}`); setT(r.data); toast.success("Bracket updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => run("bracket", has ? "Reset the bracket? All placements and scores will be cleared." : null)} disabled={busy} data-testid="gen-bracket"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white text-sm font-bold disabled:opacity-60"><GitBranch size={15} /> {has ? "Reset" : "Generate"} {FORMAT_LABEL[t.format]} ({t.max_teams} slots)</button>
        {t.format === "groups" && has && (
          <button onClick={() => run("bracket/playoffs", hasPlayoffs ? "Rebuild playoffs from current standings?" : null)} disabled={busy} data-testid="gen-playoffs"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/20 text-white text-sm font-bold">Generate Playoffs (top {t.advance_per_group} per group)</button>
        )}
        <span className="text-xs text-white/40">The bracket is created automatically from Max Teams. Place registered teams into slots below, then click "Edit" on a match to set scores and winners — winners advance automatically.</span>
      </div>
      <SlotBoard t={t} setT={setT} />
      <BracketView tournament={t} onEdit={onEdit} />
    </div>
  );
}

function ScheduleTab({ t, onEdit }) {
  const ms = (t.bracket?.matches || []).filter((m) => m.a !== "BYE" && m.b !== "BYE")
    .sort((a, b) => (a.scheduled_at || "9999").localeCompare(b.scheduled_at || "9999") || a.round - b.round);
  if (!ms.length) return <div className="text-white/40 py-12 text-center">Generate the bracket first to schedule matches.</div>;
  return (
    <div className="divide-y divide-white/5 card-surface rounded-xl" data-testid="schedule-list">
      {ms.map((m) => (
        <div key={m.id} className="flex items-center gap-4 px-4 py-3 text-sm">
          <span className="w-40 text-white/50 flex items-center gap-1.5"><CalendarClock size={13} /> {m.scheduled_at ? fmtDateTime(m.scheduled_at) : "Not scheduled"}</span>
          <span className="flex-1 text-white truncate">{m.a} <span className="text-white/30">vs</span> {m.b}</span>
          <span className="text-xs text-white/40 hidden sm:block">{m.round_name}{m.group ? ` · ${m.group}` : ""}</span>
          <ReportBadge status={m.report_status} />
          {m.status !== "completed" && <a href={`/print/qr/${t.id}?match=${m.id}`} target="_blank" rel="noreferrer" title="Print QR" className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/5" data-testid={`print-qr-${m.id}`}><QrCode size={14} /></a>}
          <span className={`text-xs font-bold uppercase ${m.status === "live" ? "text-[hsl(var(--accent))]" : m.status === "completed" ? "text-green-400" : "text-white/40"}`}>{m.status}</span>
          <button onClick={() => onEdit(m)} className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/5" data-testid={`schedule-edit-${m.id}`}><Pencil size={14} /></button>
        </div>
      ))}
    </div>
  );
}

export default function TournamentManager({ id, onDeleted }) {
  const { tournament_price_usd } = useConfig();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [tab, setTab] = useState("Details");
  const [editing, setEditing] = useState(null);
  const [pay, setPay] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const load = () => api.get(`/manage/tournaments/${id}`).then((r) => setT(r.data)).catch(() => toast.error("Tournament not found"));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!t) return <div className="py-20 text-center text-white/40">Loading…</div>;

  const quickStatus = async (status) => {
    try { const r = await api.put(`/manage/tournaments/${t.id}`, { ...pickForm(t), status }); setT(r.data); toast.success(`Status: ${status}`); } catch { toast.error("Failed"); }
  };
  const teamStatus = async (pid, status) => { try { const r = await api.put(`/manage/tournaments/${t.id}/participants/${pid}`, { status }); setT(r.data); } catch { toast.error("Failed"); } };
  const teamRemove = async (pid) => { if (!window.confirm("Remove this team?")) return; try { const r = await api.delete(`/manage/tournaments/${t.id}/participants/${pid}`); setT(r.data); } catch { toast.error("Failed"); } };
  const finish = async () => {
    if (!window.confirm("Finish & close this tournament? Registration closes, status becomes Completed and the PDF report is emailed to the organizer.")) return;
    setFinishing(true);
    try { const r = await api.post(`/manage/tournaments/${t.id}/finish`); setT(r.data.tournament); toast.success(r.data.email_sent ? "Tournament finished — report sent by email" : "Tournament finished — download your report"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setFinishing(false); }
  };
  const del = async () => { if (!window.confirm("Delete this tournament permanently?")) return; await api.delete(`/manage/tournaments/${t.id}`); toast.success("Deleted"); onDeleted?.(); };
  const logos = Object.fromEntries((t.participants || []).map((p) => [p.name, p.logo]));
  const confirmed = (t.participants || []).filter((p) => p.status === "confirmed").length;
  const openReports = (t.bracket?.matches || []).filter((m) => ["disputed", "pending_review"].includes(m.report_status)).length;

  return (
    <div data-testid="tournament-manager">
      <div className="card-surface rounded-2xl overflow-hidden mb-6">
        <div className="relative h-40">
          {t.cover && <img src={mediaUrl(t.cover)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--card))] via-black/50 to-transparent" />
          <div className="absolute bottom-4 left-5 right-5 flex flex-wrap items-end justify-between gap-3">
            <div><div className="eyebrow text-white/60">{t.game || "Game"} · {FORMAT_LABEL[t.format]}</div><h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="mgr-title">{t.name}</h1></div>
            <div className="flex items-center gap-2">
              <select value={t.status} onChange={(e) => quickStatus(e.target.value)} className="px-3 py-2 rounded-full bg-black/60 border border-white/15 text-sm text-white font-semibold" data-testid="mgr-status">
                {T_STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select>
              {t.status !== "completed" ? (
                <button onClick={finish} disabled={finishing} data-testid="mgr-finish" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-black text-sm font-bold disabled:opacity-60">{finishing ? <Loader2 size={14} className="animate-spin" /> : <Flag size={14} />} Finish & Close</button>
              ) : t.published && (
                <a href={`${API}/tournaments/${t.slug}/report.pdf`} data-testid="mgr-report" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-white text-black text-sm font-bold"><FileDown size={14} /> PDF Report</a>
              )}
              {t.published && <Link to={`/tournaments/${t.slug}`} target="_blank" className="p-2 rounded-full bg-black/60 text-white/70 hover:text-white" data-testid="mgr-view-public"><ExternalLink size={16} /></Link>}
              <button onClick={del} className="p-2 rounded-full bg-black/60 text-white/60 hover:text-red-400" data-testid="mgr-delete"><Trash2 size={16} /></button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 flex flex-wrap gap-5 text-xs text-white/50 border-t border-white/5">
          <span>{fmtDateTime(t.start_date)} → {fmtDateTime(t.end_date)}</span><span>{confirmed} / {t.max_teams} teams</span>
          <span>Registration {t.registration_open ? "open" : "closed"}</span><span data-testid="mgr-payment-status">Payment: {t.payment_status}</span>
        </div>
      </div>

      {t.result?.champion && <div className="mb-6"><ChampionBanner result={t.result} name={t.name} /></div>}
      {t.payment_status === "pending" && !user?.is_admin && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/10 px-5 py-4" data-testid="mgr-pay-banner">
          <div className="text-sm text-white/80"><b className="text-white">Payment required.</b> Your tournament stays private until the ${tournament_price_usd.toFixed(2)} creation fee is paid.</div>
          <button onClick={() => setPay(true)} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white text-black text-sm font-bold" data-testid="mgr-pay-btn"><CreditCard size={15} /> Pay ${tournament_price_usd.toFixed(2)}</button>
        </div>
      )}

      <div className="flex gap-1 border-b border-white/10 mb-6 overflow-x-auto">
        {TABS.map((x) => (
          <button key={x} onClick={() => setTab(x)} data-testid={`mgr-tab-${x.split(" ")[0].toLowerCase()}`}
            className={`px-4 py-3 text-sm font-semibold border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === x ? "border-[hsl(var(--primary))] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
            {x}{x === "Teams" ? ` (${confirmed})` : ""}{x === "Reports" && openReports > 0 ? <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-amber-500 text-black text-[10px] font-bold" data-testid="reports-alert">{openReports}</span> : null}
          </button>
        ))}
      </div>

      {tab === "Details" && <DetailsTab key={t.updated_at} t={t} onSaved={setT} />}
      {tab === "Teams" && <TeamsList participants={t.participants} onStatus={teamStatus} onRemove={teamRemove} onEdit={setEditTeam} showRejected />}
      {editTeam && <TeamEditor tid={t.id} team={editTeam} onClose={() => setEditTeam(null)} onSaved={(d) => { setT(d); setEditTeam(null); }} />}
      {tab === "Bracket & Scores" && <BracketTab t={t} setT={setT} onEdit={setEditing} />}
      {tab === "Schedule" && <ScheduleTab t={t} onEdit={setEditing} />}
      {tab === "Reports" && <ReportsPanel t={t} setT={setT} />}
      {tab === "Share" && <SharePanel tournament={t} />}

      {editing && <MatchEditor tid={t.id} match={editing} logos={logos} allowDraw={["rr", "group"].includes(editing.section)} onClose={() => setEditing(null)} onSaved={(d) => { setT(d); setEditing(null); }} />}
      {pay && <CheckoutModal tournament={t} price={tournament_price_usd} onClose={() => { setPay(false); load(); }} onPaid={() => { load(); toast.success("Payment received — tournament is live!"); }} />}
    </div>
  );
}
